import { Tooltip } from 'antd';
import type { CSSProperties, ReactNode } from 'react';
import type {
  WorkflowRunHistoryItem,
  WorkflowRunStatus,
} from '@/apis/logiblocksAPI';
import { formatDateTime } from '../dateTimeUtils';
import useUserDateTimeFormat from '@/hooks/useUserDateTimeFormat';
import {
  RUN_COMPLETED_COLOR,
  RUN_FAILED_COLOR,
  TOOLTIP_BACKGROUND_COLOR,
} from '../styles';
import styles from './RunFailuresTooltip.module.css';

// Status-line color for the outcomes the run visuals color; other statuses
// keep the card's default text color.
const RUN_STATUS_COLOR: Partial<Record<WorkflowRunStatus, string>> = {
  FAILED: RUN_FAILED_COLOR,
  // A canceled run's node is failed, so the canvas already paints it red.
  CANCELED: RUN_FAILED_COLOR,
  COMPLETED: RUN_COMPLETED_COLOR,
};

// The status line's wording for a run status, matching StatusIcon's labels.
function formatRunStatus(status: WorkflowRunStatus): string {
  switch (status) {
    case 'FAILED':
      return 'Failed';
    case 'CANCELED':
      return 'Canceled';
    case 'COMPLETED':
      return 'Completed';
    case 'WAIT_EXPIRED':
      return 'Wait expired';
    case 'SUSPENDED':
      return 'Suspended';
    case 'RUNNING':
      return 'Running';
    default:
      console.warn(`Unexpected workflow run status: ${status}`);
      return 'Running';
  }
}

/** A run's status, one line per failed node's error, and when it ran. */
export function RunFailuresCard({ run }: { run: WorkflowRunHistoryItem }) {
  const { dateFormat } = useUserDateTimeFormat();
  const reasons = run.failure_reasons;
  return (
    <div className={styles.card}>
      <div className={styles.section}>
        <div className={styles.label}>Status</div>
        <div
          className={styles.status}
          // eslint-disable-next-line react/forbid-dom-props -- the run colors are shared constants, also used on the canvas
          style={{ color: RUN_STATUS_COLOR[run.status] }}
        >
          {formatRunStatus(run.status)}
        </div>
      </div>
      <div className={styles.section}>
        <div className={styles.label}>
          {reasons.length > 1 ? 'Reasons' : 'Reason'}
        </div>
        {reasons.length === 0 ? (
          <div className={styles.value}>No error message recorded</div>
        ) : (
          reasons.map((reason) => (
            <div
              key={`${reason.node_id}:${reason.error_message ?? ''}`}
              className={styles.value}
            >
              {reason.error_message ?? 'No error message recorded'}
            </div>
          ))
        )}
      </div>
      <div className={styles.section}>
        <div className={styles.label}>Time</div>
        <div className={styles.value}>
          {formatDateTime(run.started_at, dateFormat)}
        </div>
      </div>
    </div>
  );
}

// The tooltip's own frame
const CARD_BODY_STYLE: CSSProperties = {
  padding: '16px 20px',
  borderRadius: 8,
};

/**
 * Wraps a run row so hovering it reveals the run's failures card.
 */
export function RunFailuresTooltip({
  run,
  children,
}: {
  run: WorkflowRunHistoryItem;
  children: ReactNode;
}) {
  return (
    <Tooltip
      color={TOOLTIP_BACKGROUND_COLOR}
      styles={{ container: CARD_BODY_STYLE }}
      title={<RunFailuresCard run={run} />}
    >
      {children}
    </Tooltip>
  );
}
